import { useEffect, useState } from 'react'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import styles from './styles.module.css'
import { getLocationList } from './api'

interface ILocation {
  name: string
  objectId: string
  active?: boolean
}

interface LocationFilterProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
  showInactive?: boolean
}

function LocationFilter({
  value,
  onChange,
  placeholder = 'Default (all)',
  disabled,
  showInactive = false,
}: LocationFilterProps) {
  const [locations, setLocations] = useState<ILocation[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    async function fetchLocations() {
      try {
        const data = await getLocationList()
        setLocations(data)
      } catch (error) {
        console.error('Failed to fetch locations:', error)
      } finally {
        setIsLoading(false)
      }
    }
    fetchLocations()
  }, [])

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value)
  }

  // Sort and filter locations
  const sortedLocations = locations
    .filter((location: ILocation) => location.name)
    .filter((location: ILocation) => showInactive || location.active)
    .sort((a: ILocation, b: ILocation) => a.name?.localeCompare(b.name))

  // Convert to items format matching DropdownFilter
  const items = sortedLocations.map((location: ILocation) => ({
    key: location.name, // Use name as key for filtering by name
    value: location.name,
  }))

  const data = [{ key: '', value: placeholder }].concat(items)

  let menuItems

  if (isLoading) {
    menuItems = (
      <MenuItem
        value=''
        className={styles.dropDownOption}
      >
        Loading...
      </MenuItem>
    )
  } else {
    menuItems = data.filter(Boolean).map(({ key, value: displayValue }) => {
      return (
        <MenuItem
          key={key}
          value={key}
          className={styles.dropDownOption}
        >
          {displayValue}
        </MenuItem>
      )
    })
  }

  return (
    <div>
      <TextField
        className={styles.dropDownSelect}
        select
        fullWidth
        size='small'
        value={value}
        onChange={handleChange}
        disabled={disabled || isLoading}
        slotProps={{
          select: {
            displayEmpty: true,
            sx: {
              fontWeight: 'bold',
              textAlign: 'left',
              color: value === '' ? 'var(--filter-placeholder)' : 'inherit',
            },
            MenuProps: {
              PaperProps: {
                style: {
                  maxHeight: 400,
                  width: 250,
                },
              },
            },
          },
        }}
        sx={{
          minWidth: '100px',
          maxWidth: '200px',
          display: 'flex',
          justifyContent: 'flex-start',
          backgroundColor: 'var(--filter-bg)',
          height: '35px',
          '& .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
        }}
      >
        {menuItems}
      </TextField>
    </div>
  )
}

export default LocationFilter
